import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Briefcase, DollarSign, Calendar, FileText, TrendingUp,
  LogOut, Upload, Loader2, Sun, Moon, Eye, X, Download, FileDown,
  ChevronLeft, ChevronRight, Filter, Clock
} from 'lucide-react';
import { api } from '../utils/apiClient';
import apiClient from '../utils/apiClient';
import { secureStorage } from '../utils/securityUtils';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';

axios.defaults.withCredentials = true;

const THEME_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];
const MONTHS = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

const formatCurrency = (value) =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(value || 0);

// --- MODAL VISOR DE ARCHIVOS ---
const FileViewerModal = ({ expediente, onClose }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const res = await api.get(`/expedientes/${expediente}/files`);
        setFiles(res.archivos || []);
      } catch { /* silencioso */ }
      finally { setLoading(false); }
    };
    fetchFiles();
  }, [expediente]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 w-full max-w-md rounded-3xl shadow-2xl overflow-hidden border border-slate-200 dark:border-gray-800">
        <div className="px-6 py-4 border-b border-slate-100 dark:border-gray-800 flex justify-between items-center bg-slate-50 dark:bg-gray-800/50">
          <div>
            <h3 className="font-bold text-slate-800 dark:text-white">Expediente Digital</h3>
            <p className="text-[10px] text-blue-500 font-bold uppercase tracking-widest">{expediente}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 dark:hover:bg-gray-700 rounded-full transition-colors">
            <X size={20} className="text-slate-500" />
          </button>
        </div>
        <div className="p-6 max-h-[400px] overflow-y-auto custom-scrollbar">
          {loading ? (
            <div className="flex flex-col items-center py-10">
              <Loader2 className="animate-spin text-blue-500 mb-2" />
              <p className="text-xs text-slate-400 uppercase font-bold">Buscando documentos...</p>
            </div>
          ) : files.length > 0 ? (
            <div className="space-y-3">
              {files.map((file, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 rounded-2xl border border-slate-100 dark:border-gray-800 bg-slate-50 dark:bg-gray-800/30 hover:border-blue-300 transition-all">
                  <div className="flex items-center gap-3 overflow-hidden">
                    <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg text-blue-600"><FileText size={18} /></div>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">{file}</span>
                  </div>
                  <a href={`${apiClient.defaults.baseURL}/expedientes/${expediente}/files/${file}`} target="_blank" rel="noopener noreferrer" className="p-2 text-slate-400 hover:text-blue-500 transition-colors">
                    <Download size={18} />
                  </a>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10">
              <FileText size={40} className="mx-auto text-slate-200 dark:text-gray-800 mb-3" />
              <p className="text-sm text-slate-400 italic">No hay archivos cargados.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const AbogadoDashboard = ({ loggedInUser, onLogout, embedded = false }) => {
  // Diner 2026-04-27: la fuente real es 'lp_user_display' (App.jsx setea ahi al login).
  // Fallback a userData (legacy/secureStorage) por compatibilidad.
  const savedUser = (() => {
    try {
      const raw = localStorage.getItem('lp_user_display');
      if (raw) return JSON.parse(raw);
    } catch (e) {}
    try { return secureStorage.getItem('userData') || {}; } catch (e) { return {}; }
  })();
  const currentUser = loggedInUser || savedUser.nombre || "Abogado";
  const currentDoc = String(savedUser.num_doc || '');

  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState(null);
  const [viewingExpediente, setViewingExpediente] = useState(null);
  const [myInvoices, setMyInvoices] = useState([]);
  const [myCases, setMyCases] = useState([]);
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));

  // Filtros
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1); // 1-12
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [filterMode, setFilterMode] = useState('month'); // 'month' | 'range'

  // Cuentas de cobro y documentos de soporte
  const [cuentasCobro, setCuentasCobro] = useState(null);
  const [docsSoporte, setDocsSoporte] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);

  const toggleDarkMode = () => {
    const newDark = !isDark;
    setIsDark(newDark);
    if (newDark) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
  };

  const selectedMonthStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;

  const fetchData = async () => {
    setLoading(true);
    try {
      // Backend ya filtra por abogado segun rol, no necesitamos filtrar en frontend
      const [casosRes, facturasRes] = await Promise.all([
        api.get('/casos'),
        api.get('/factura')
      ]);

      const casos = Array.isArray(casosRes) ? casosRes : (casosRes.data || []);
      const facturas = Array.isArray(facturasRes) ? facturasRes : (facturasRes.data || []);

      setMyCases(casos);
      setMyInvoices(facturas.map(inv => ({
        ...inv,
        montoPropio: Number(inv.honorario_abogado) || 0,
        estadoNormalizado: (inv.cierre_servicio || '').toLowerCase()
      })));
    } catch (err) {
      console.error("Error cargando dashboard:", err);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (!currentDoc) return;
    const fetchDocumentos = async () => {
      setLoadingDocs(true);
      try {
        const [ccRes, docsRes] = await Promise.allSettled([
          api.get(`/cuenta-cobro/${currentDoc}`),
          api.get(`/abogado/${currentDoc}/documentos`),
        ]);
        if (ccRes.status === 'fulfilled') setCuentasCobro(ccRes.value);
        if (docsRes.status === 'fulfilled') setDocsSoporte(docsRes.value.archivos || []);
      } catch { /* silencioso */ }
      finally { setLoadingDocs(false); }
    };
    fetchDocumentos();
  }, [currentDoc]);

  const filteredData = useMemo(() => {
    let casesFiltered, invoicesFiltered;

    if (filterMode === 'range' && fechaDesde && fechaHasta) {
      casesFiltered = myCases.filter(c => c.fecha && c.fecha >= fechaDesde && c.fecha <= fechaHasta);
      invoicesFiltered = myInvoices.filter(i => i.fecha && i.fecha >= fechaDesde && i.fecha <= fechaHasta);
    } else {
      casesFiltered = myCases.filter(c => c.fecha && c.fecha.startsWith(selectedMonthStr));
      invoicesFiltered = myInvoices.filter(i => i.fecha && i.fecha.startsWith(selectedMonthStr));
    }

    return {
      cases: casesFiltered,
      invoices: invoicesFiltered,
      stats: {
        total: casesFiltered.length,
        income: invoicesFiltered.reduce((a, b) => a + b.montoPropio, 0),
        pending: invoicesFiltered.filter(i => !i.estadoNormalizado.includes('exitoso')).reduce((a, b) => a + b.montoPropio, 0)
      }
    };
  }, [myCases, myInvoices, selectedMonthStr, filterMode, fechaDesde, fechaHasta]);

  const chartData = useMemo(() => {
    const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
    const dailyDataMap = {};
    for (let i = 1; i <= daysInMonth; i++) {
      dailyDataMap[`${selectedMonthStr}-${String(i).padStart(2, '0')}`] = 0;
    }
    filteredData.cases.forEach(c => { if (dailyDataMap[c.fecha] !== undefined) dailyDataMap[c.fecha] += 1; });
    const area = Object.keys(dailyDataMap).sort().map(date => ({ name: date.split('-')[2], total: dailyDataMap[date] }));

    const t = {};
    filteredData.cases.forEach(c => { const s = c.tipo_servicio || 'OTRO'; t[s] = (t[s] || 0) + 1; });
    const pie = Object.keys(t).map(k => ({ name: k, value: t[k] }));

    return { area, pie };
  }, [filteredData.cases, selectedMonthStr, selectedYear, selectedMonth]);

  const navigateMonth = (dir) => {
    let m = selectedMonth + dir;
    let y = selectedYear;
    if (m < 1) { m = 12; y--; }
    if (m > 12) { m = 1; y++; }
    setSelectedMonth(m);
    setSelectedYear(y);
  };

  const handleDownloadPDF = async (cc, filename) => {
    try {
      const resp = await apiClient.get(`/cuenta-cobro/${cc}/download/${filename}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert('Error al descargar el archivo.'); }
  };

  const handleDownloadDoc = async (cc, filename) => {
    try {
      const resp = await apiClient.get(`/abogado/${cc}/documento/${filename}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a'); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert('Error al descargar el archivo.'); }
  };

  const handleFileUpload = async (expediente, files) => {
    if (!files || files.length === 0) return;
    setUploadingId(expediente);
    const formData = new FormData();
    Array.from(files).forEach(f => formData.append('files', f));
    try {
      // N9-bis: backend devuelve 'archivos' (lista de filenames guardados).
      const resp = await apiClient.post(`/expedientes/${expediente}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const data = (resp && resp.data) || {};
      const rechazados = Array.isArray(data.rechazados) ? data.rechazados : [];
      const guardados = Array.isArray(data.archivos) ? data.archivos : null;
      const subidos = guardados ? guardados.length : Math.max(files.length - rechazados.length, 0);
      if (rechazados.length === 0) {
        alert(`Subido: ${subidos} archivo(s).`);
      } else {
        const lineas = rechazados.map((r) => `  - ${r.nombre || r.archivo || r.filename || 'archivo'}: ${r.motivo || r.reason || 'rechazado'}`).join('\n');
        alert(`Subidos: ${subidos}\nRechazados: ${rechazados.length}\n\nDetalle:\n${lineas}`);
      }
      fetchData();
    } catch (err) {
      const msg = (err && err.response && err.response.data && err.response.data.error) || 'Error al subir.';
      alert(msg);
    }
    finally { setUploadingId(null); }
  };

  if (loading) return (
    <div className={`${embedded ? 'h-full' : 'h-screen'} bg-gray-950 flex flex-col items-center justify-center text-white`}>
      <Loader2 className="animate-spin mb-4 text-blue-600" size={40} />
      <p className="font-bold uppercase tracking-widest text-xs animate-pulse">Sincronizando...</p>
    </div>
  );

  const statCards = [
    { label: 'Casos', value: filteredData.stats.total, icon: Briefcase, color: 'blue', gradient: 'from-blue-500 to-blue-700' },
    { label: 'Honorarios', value: formatCurrency(filteredData.stats.income), icon: DollarSign, color: 'emerald', gradient: 'from-emerald-500 to-emerald-700' },
    { label: 'Pendientes', value: formatCurrency(filteredData.stats.pending), icon: Clock, color: 'amber', gradient: 'from-amber-500 to-amber-700' },
  ];

  return (
    <div className={`${embedded ? 'h-full overflow-auto' : 'h-screen overflow-hidden'} bg-slate-50 dark:bg-gray-950 flex flex-col font-sans transition-colors duration-500`}>

      {viewingExpediente && <FileViewerModal expediente={viewingExpediente} onClose={() => setViewingExpediente(null)} />}

      {!embedded && (
        <header className="bg-white dark:bg-gray-900 border-b border-slate-200 dark:border-gray-800 px-6 py-3 flex justify-between items-center shadow-sm z-10">
          <div className="flex items-center gap-4">
            <div className="bg-gradient-to-br from-blue-500 to-blue-700 p-2.5 rounded-xl text-white shadow-lg"><Briefcase size={20} /></div>
            <div>
              <h1 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Panel Abogado</h1>
              <p className="text-lg font-bold text-slate-800 dark:text-white leading-tight">{currentUser}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={toggleDarkMode} className="p-2.5 rounded-xl bg-slate-100 dark:bg-gray-800 text-slate-600 dark:text-amber-400 hover:scale-105 transition-all">
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button onClick={onLogout} className="p-2.5 bg-red-50 dark:bg-red-900/20 text-red-600 rounded-xl hover:bg-red-600 hover:text-white transition-all shadow-sm"><LogOut size={18} /></button>
          </div>
        </header>
      )}

      <main className="flex-1 overflow-y-auto p-4 lg:p-6 space-y-6">

        {/* ─── FILTROS ─── */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-slate-200 dark:border-gray-800 shadow-sm p-4">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-slate-400" />
              <div className="flex bg-slate-100 dark:bg-gray-800 rounded-lg p-0.5">
                <button onClick={() => setFilterMode('month')}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${filterMode === 'month' ? 'bg-white dark:bg-gray-700 text-blue-600 shadow-sm' : 'text-slate-500'}`}>
                  Mes
                </button>
                <button onClick={() => setFilterMode('range')}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${filterMode === 'range' ? 'bg-white dark:bg-gray-700 text-blue-600 shadow-sm' : 'text-slate-500'}`}>
                  Rango
                </button>
              </div>
            </div>

            {filterMode === 'month' ? (
              <div className="flex items-center gap-2">
                <button onClick={() => navigateMonth(-1)} className="p-1.5 rounded-lg bg-slate-100 dark:bg-gray-800 hover:bg-slate-200 dark:hover:bg-gray-700 transition-colors">
                  <ChevronLeft size={16} className="text-slate-600 dark:text-gray-400" />
                </button>
                <div className="flex items-center gap-1.5 bg-slate-100 dark:bg-gray-800 rounded-lg px-3 py-1.5">
                  <Calendar size={14} className="text-blue-500" />
                  <select value={selectedMonth} onChange={e => setSelectedMonth(Number(e.target.value))}
                    className="bg-transparent text-sm font-bold text-slate-700 dark:text-white border-none focus:ring-0 cursor-pointer">
                    {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                  </select>
                  <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))}
                    className="bg-transparent text-sm font-bold text-slate-700 dark:text-white border-none focus:ring-0 cursor-pointer">
                    {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <button onClick={() => navigateMonth(1)} className="p-1.5 rounded-lg bg-slate-100 dark:bg-gray-800 hover:bg-slate-200 dark:hover:bg-gray-700 transition-colors">
                  <ChevronRight size={16} className="text-slate-600 dark:text-gray-400" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <input type="date" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)}
                  className="text-xs bg-slate-100 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-slate-700 dark:text-white" />
                <span className="text-xs text-slate-400">a</span>
                <input type="date" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)}
                  className="text-xs bg-slate-100 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-slate-700 dark:text-white" />
              </div>
            )}
          </div>
        </div>

        {/* ─── STATS CARDS ─── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {statCards.map((card, i) => (
            <div key={i} className="bg-white dark:bg-gray-900 rounded-2xl border border-slate-200 dark:border-gray-800 shadow-sm p-5 flex items-center gap-4">
              <div className={`bg-gradient-to-br ${card.gradient} p-3 rounded-xl text-white shadow-lg`}>
                <card.icon size={22} />
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{card.label}</p>
                <p className="text-2xl font-black text-slate-800 dark:text-white">{typeof card.value === 'number' ? card.value : card.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ─── CHARTS ─── */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3 bg-white dark:bg-gray-900 p-6 rounded-2xl border border-slate-200 dark:border-gray-800 shadow-sm">
            <h3 className="text-xs font-bold text-slate-400 uppercase mb-4">Flujo Diario: {MONTHS[selectedMonth - 1]} {selectedYear}</h3>
            <div style={{ width: '100%', height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData.area}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDark ? "#334155" : "#e2e8f0"} />
                  <XAxis dataKey="name" tick={{ fill: isDark ? '#94a3b8' : '#64748b', fontSize: 10 }} />
                  <YAxis tick={{ fill: isDark ? '#94a3b8' : '#64748b', fontSize: 10 }} />
                  <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: 'none', borderRadius: '12px' }} />
                  <Area type="monotone" dataKey="total" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.1} strokeWidth={2} name="Casos" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 p-6 rounded-2xl border border-slate-200 dark:border-gray-800 shadow-sm">
            <h3 className="text-xs font-bold text-slate-400 uppercase mb-4 text-center">Especialidades</h3>
            <div style={{ width: '100%', height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={chartData.pie} innerRadius={45} outerRadius={65} dataKey="value" stroke="none" paddingAngle={6}>
                    {chartData.pie.map((_, i) => <Cell key={i} fill={THEME_COLORS[i % THEME_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: '10px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* ─── CASOS + HONORARIOS ─── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Casos */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-slate-200 dark:border-gray-800 shadow-sm overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-slate-100 dark:border-gray-800 bg-slate-50/50 dark:bg-gray-800/50 flex items-center justify-between">
              <h3 className="font-bold text-slate-800 dark:text-white flex items-center gap-2"><FileText size={18} className="text-blue-600" /> Casos</h3>
              <span className="text-[10px] font-bold text-slate-400 bg-slate-100 dark:bg-gray-800 px-2 py-1 rounded-lg">{filteredData.cases.length}</span>
            </div>
            <div className="overflow-x-auto max-h-[400px] overflow-y-auto custom-scrollbar">
              <table className="w-full text-left text-sm">
                <thead className="text-[10px] uppercase text-slate-400 font-bold bg-slate-50/50 dark:bg-gray-900/50 sticky top-0">
                  <tr>
                    <th className="px-5 py-3">Expediente</th>
                    <th className="px-5 py-3">Aseguradora</th>
                    <th className="px-5 py-3">Fecha</th>
                    <th className="px-5 py-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
                  {filteredData.cases.slice().reverse().map((c, idx) => (
                    <tr key={c.expediente || idx} className="hover:bg-blue-50/50 dark:hover:bg-gray-800/40 transition-colors">
                      <td className="px-5 py-3.5 font-bold text-slate-700 dark:text-gray-200 text-xs">{c.expediente}</td>
                      <td className="px-5 py-3.5"><span className="text-[10px] font-bold uppercase px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-md">{c.aseguradora}</span></td>
                      <td className="px-5 py-3.5 text-xs text-slate-500">{c.fecha}</td>
                      <td className="px-5 py-3.5 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button onClick={() => setViewingExpediente(c.expediente)} className="p-1.5 rounded-lg border border-slate-200 dark:border-gray-700 text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-all">
                            <Eye size={14} />
                          </button>
                          <label className={`cursor-pointer p-1.5 rounded-lg border transition-all ${uploadingId === c.expediente ? 'bg-blue-600 text-white border-blue-600' : 'border-slate-200 dark:border-gray-700 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20'}`}>
                            {uploadingId === c.expediente ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                            <input type="file" className="hidden" multiple onChange={(e) => handleFileUpload(c.expediente, e.target.files)} />
                          </label>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredData.cases.length === 0 && (
                    <tr><td colSpan={4} className="text-center py-10 text-sm text-slate-400">Sin casos en este periodo</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Honorarios */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2 px-1">
              <TrendingUp size={18} className="text-emerald-600" />
              <h3 className="font-bold text-slate-800 dark:text-white">Honorarios</h3>
              <span className="text-[10px] font-bold text-slate-400 bg-slate-100 dark:bg-gray-800 px-2 py-1 rounded-lg">{filteredData.invoices.length}</span>
            </div>
            <div className="max-h-[400px] overflow-y-auto pr-1 space-y-2.5 custom-scrollbar">
              {filteredData.invoices.map((inv, idx) => (
                <div key={idx} className="bg-white dark:bg-gray-900 p-4 rounded-2xl border border-slate-200 dark:border-gray-800 flex items-center justify-between shadow-sm hover:border-emerald-300 dark:hover:border-emerald-700 transition-all">
                  <div className="flex items-center gap-3">
                    <div className="bg-emerald-50 dark:bg-emerald-900/20 p-2.5 rounded-xl text-emerald-600"><Calendar size={16} /></div>
                    <div>
                      <p className="text-[10px] text-slate-400 font-bold uppercase">{inv.aseguradora}</p>
                      <p className="font-bold text-slate-800 dark:text-white text-sm">Exp: {inv.expediente}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-black text-emerald-600">{formatCurrency(inv.montoPropio)}</p>
                    <span className="text-[10px] text-slate-400">{inv.fecha}</span>
                  </div>
                </div>
              ))}
              {filteredData.invoices.length === 0 && (
                <div className="text-center py-10 text-sm text-slate-400">Sin honorarios en este periodo</div>
              )}
            </div>
          </div>
        </div>

        {/* ─── CUENTAS DE COBRO + COMPROBANTES ─── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-6">
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-slate-200 dark:border-gray-800 shadow-sm overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-slate-100 dark:border-gray-800 bg-slate-50/50 dark:bg-gray-800/50 flex items-center justify-between">
              <h3 className="font-bold text-slate-800 dark:text-white flex items-center gap-2">
                <DollarSign size={18} className="text-violet-600" /> Mis Cuentas de Cobro
              </h3>
              {loadingDocs && <Loader2 size={14} className="animate-spin text-slate-400" />}
            </div>
            <div className="flex-1 overflow-y-auto custom-scrollbar max-h-64">
              {!cuentasCobro || !cuentasCobro.archivos?.length ? (
                <div className="flex flex-col items-center justify-center py-10 text-slate-300 dark:text-gray-600">
                  <FileText size={32} className="mb-2 opacity-50" />
                  <p className="text-xs">Sin cuentas de cobro generadas</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-50 dark:divide-gray-800">
                  {cuentasCobro.archivos.map((archivo, i) => (
                    <div key={i} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 dark:hover:bg-gray-800/40 transition-colors">
                      <div className="flex items-center gap-3 min-w-0">
                        <FileText size={15} className="text-red-500 flex-shrink-0" />
                        <span className="text-xs font-mono text-slate-600 dark:text-gray-300 truncate">{archivo}</span>
                      </div>
                      <button onClick={() => handleDownloadPDF(currentDoc, archivo)}
                        className="flex-shrink-0 p-2 rounded-xl border border-slate-200 dark:border-gray-700 text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-all ml-2">
                        <FileDown size={15} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-slate-200 dark:border-gray-800 shadow-sm overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-slate-100 dark:border-gray-800 bg-slate-50/50 dark:bg-gray-800/50">
              <h3 className="font-bold text-slate-800 dark:text-white flex items-center gap-2">
                <FileDown size={18} className="text-emerald-600" /> Comprobantes de Pago
              </h3>
            </div>
            <div className="flex-1 overflow-y-auto custom-scrollbar max-h-64">
              {!docsSoporte.length ? (
                <div className="flex flex-col items-center justify-center py-10 text-slate-300 dark:text-gray-600">
                  <Download size={32} className="mb-2 opacity-50" />
                  <p className="text-xs">Sin comprobantes cargados</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-50 dark:divide-gray-800">
                  {docsSoporte.map((archivo, i) => (
                    <div key={i} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 dark:hover:bg-gray-800/40 transition-colors">
                      <div className="flex items-center gap-3 min-w-0">
                        <FileText size={15} className="text-emerald-500 flex-shrink-0" />
                        <span className="text-xs font-mono text-slate-600 dark:text-gray-300 truncate">{archivo}</span>
                      </div>
                      <button onClick={() => handleDownloadDoc(currentDoc, archivo)}
                        className="flex-shrink-0 p-2 rounded-xl border border-slate-200 dark:border-gray-700 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-all ml-2">
                        <Download size={15} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

      </main>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; }
      `}</style>
    </div>
  );
};

export default AbogadoDashboard;
